import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  seedDemoInventory,
} from "@/lib/inventory.functions";
import {
  listOnHandBalances, listFacilityLocations,
  quickCountAndPost, quickStockTransfer, startCycleCountForLocations,
} from "@/lib/inventory-catalog.functions";
import {
  createInventoryAdjustment, postInventoryAdjustment,
} from "@/lib/inventory-adjustments.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, ClipboardCheck, ArrowLeftRight, SlidersHorizontal } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ContextualTip } from "@/components/kb/contextual-tip";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { FilterBar } from "@/components/ui/filter-bar";
import { PageHeader } from "@/components/ui/page-header";
import { ExportMenu } from "@/components/ui/export-menu";
import {
  useSkuPlanningSummary, RiskCell, DaysOfSupplyCell, WeeklyVelocityCell,
  ProjectedStockoutCell, OpenRecommendationCell,
} from "@/components/inventory/planning-cells";

export const Route = createFileRoute("/_authenticated/app/inventory/on-hand")({ component: OnHandPage });

type Row = { sku_id: string; location_id: string; lot: string | null; qty_on_hand: number };

function OnHandPage() {
  const { tenantId, facilityId, customerId, facilities } = useScope();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const seedFn = useServerFn(seedDemoInventory);
  const listBalancesFn = useServerFn(listOnHandBalances);
  const listDestLocsFn = useServerFn(listFacilityLocations);
  const quickCountFn = useServerFn(quickCountAndPost);
  const quickTransferFn = useServerFn(quickStockTransfer);
  const startCountFn = useServerFn(startCycleCountForLocations);
  const createAdjFn = useServerFn(createInventoryAdjustment);
  const postAdjFn = useServerFn(postInventoryAdjustment);
  const [seeding, setSeeding] = useState(false);
  const [starting, setStarting] = useState(false);
  const [search, setSearch] = useState("");
  // Quick count
  const [countRow, setCountRow] = useState<Row | null>(null);
  const [countQty, setCountQty] = useState("");
  const [counting, setCounting] = useState(false);
  // Quick transfer
  const [xferRow, setXferRow] = useState<Row | null>(null);
  const [xferDestFac, setXferDestFac] = useState<string>("");
  const [xferDestLoc, setXferDestLoc] = useState<string>("");
  const [xferQty, setXferQty] = useState("");
  const [xfering, setXfering] = useState(false);
  // Adjust
  const [adjRow, setAdjRow] = useState<Row | null>(null);
  const [adjDelta, setAdjDelta] = useState("");
  const [adjReason, setAdjReason] = useState<string>("manual_other");
  const [adjNotes, setAdjNotes] = useState("");
  const [adjusting, setAdjusting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["balances", tenantId, facilityId, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listBalancesFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null } }),
    placeholderData: keepPreviousData,
  });
  const skus = data?.skus;
  const locs = data?.locations;
  const skuName = (id: string) => { const s = skus?.find((x) => x.id === id); return s ? `${s.sku_code} — ${s.name}` : "—"; };
  const locCode = (id: string) => locs?.find((x) => x.id === id)?.code ?? "—";
  const rows = (data?.rows ?? []).filter((r) => r.qty_on_hand !== 0);
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) =>
      skuName(r.sku_id!).toLowerCase().includes(q) ||
      locCode(r.location_id!).toLowerCase().includes(q) ||
      (r.lot ?? "").toLowerCase().includes(q)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, search, skus, locs]);

  const skuIdsOnPage = useMemo(
    () => Array.from(new Set(filteredRows.map((r) => r.sku_id).filter(Boolean) as string[])),
    [filteredRows],
  );
  const planningMap = useSkuPlanningSummary({ tenantId, facilityId, skuIds: skuIdsOnPage });

  const seed = async () => {
    if (!tenantId || !facilityId) return;
    if (!confirm("Seed demo inventory for this facility? Adds receipt entries for up to 10 SKUs.")) return;
    setSeeding(true);
    try {
      const { result } = await runServerFn(
        () => seedFn({ data: { tenantId, facilityId } }),
        "Seed demo inventory",
      );
      const r = result as { inserted?: number; skipped?: boolean } | null;
      if (r?.skipped) toast.info("Inventory already exists — skipped");
      else toast.success(`Seeded ${r?.inserted ?? 0} inventory rows`);
      qc.invalidateQueries({ queryKey: ["balances", tenantId, facilityId, customerId] });
    } catch { /* toast handled */ } finally { setSeeding(false); }
  };

  // Destination locations for the quick-transfer dialog
  const { data: xferDestLocs = [] } = useQuery({
    queryKey: ["xfer-dest-locs", xferDestFac],
    enabled: !!xferDestFac,
    queryFn: () => listDestLocsFn({ data: { facilityId: xferDestFac, activeOnly: true } }),
  });

  const openCount = (r: Row) => {
    setCountRow(r); setCountQty(String(r.qty_on_hand));
  };

  const performCount = async () => {
    if (!countRow || !tenantId || !facilityId) return;
    const counted = Number(countQty);
    if (!Number.isFinite(counted) || counted < 0) return toast.error("Enter a valid count");
    setCounting(true);
    try {
      const name = `Quick count ${new Date().toLocaleString()}`;
      const res = await runServerFn(
        () => quickCountFn({ data: {
          tenantId, facilityId,
          skuId: countRow.sku_id, locationId: countRow.location_id,
          lot: countRow.lot,
          expectedQty: countRow.qty_on_hand, countedQty: counted, name,
        } }),
        "Post count",
      );
      const delta = res?.delta ?? 0;
      toast.success(delta === 0 ? "Count posted (no variance)" : `Count posted (${delta > 0 ? "+" : ""}${delta})`);
      setCountRow(null); setCountQty("");
      qc.invalidateQueries({ queryKey: ["balances", tenantId, facilityId, customerId] });
    } catch { /* toast handled */ } finally { setCounting(false); }
  };

  const openXfer = (r: Row) => {
    setXferRow(r); setXferDestFac(facilityId ?? ""); setXferDestLoc(""); setXferQty(String(r.qty_on_hand));
  };

  const openAdjust = (r: Row) => {
    setAdjRow(r); setAdjDelta(""); setAdjReason("manual_other"); setAdjNotes("");
  };

  const performAdjust = async () => {
    if (!adjRow || !tenantId || !facilityId) return;
    const delta = Number(adjDelta);
    if (!Number.isInteger(delta) || delta === 0) return toast.error("Enter a non-zero whole number");
    if (!adjNotes.trim() || adjNotes.trim().length < 3) return toast.error("Notes are required");
    setAdjusting(true);
    try {
      const created = await runServerFn(
        () => createAdjFn({ data: {
          tenantId, facilityId, customerId: customerId ?? null,
          skuId: adjRow.sku_id, locationId: adjRow.location_id,
          lot: adjRow.lot, qtyDelta: delta,
          reason: adjReason as any, notes: adjNotes.trim(),
        } }),
        "Create adjustment",
      );
      if (created?.requiresApproval) {
        toast.info("Adjustment created — awaiting manager approval");
      } else {
        await runServerFn(
          () => postAdjFn({ data: { id: created!.id } }),
          "Post adjustment",
        );
        toast.success(`Adjustment posted (${delta > 0 ? "+" : ""}${delta})`);
        qc.invalidateQueries({ queryKey: ["balances", tenantId, facilityId, customerId] });
      }
      setAdjRow(null);
    } catch { /* toast handled */ } finally { setAdjusting(false); }
  };

  const performXfer = async () => {
    if (!xferRow || !tenantId || !facilityId) return;
    const q = Number(xferQty);
    if (!Number.isFinite(q) || q <= 0) return toast.error("Enter a valid qty > 0");
    if (q > xferRow.qty_on_hand) return toast.error(`Only ${xferRow.qty_on_hand} on hand`);
    if (!xferDestFac || !xferDestLoc) return toast.error("Pick destination");
    if (xferDestFac === facilityId && xferDestLoc === xferRow.location_id) return toast.error("Source and destination must differ");
    setXfering(true);
    try {
      const ref = `XFR-${Date.now()}`;
      await runServerFn(
        () => quickTransferFn({ data: {
          tenantId,
          sourceFacilityId: facilityId, destFacilityId: xferDestFac,
          skuId: xferRow.sku_id,
          sourceLocationId: xferRow.location_id, destLocationId: xferDestLoc,
          lot: xferRow.lot, qty: q, reference: ref,
        } }),
        "Post transfer",
      );
      toast.success(`Transferred ${q} to ${xferDestLocs.find((l) => l.id === xferDestLoc)?.code ?? "destination"}`);
      setXferRow(null);
      qc.invalidateQueries({ queryKey: ["balances", tenantId, facilityId, customerId] });
    } catch { /* toast handled */ } finally { setXfering(false); }
  };

  const startCount = async () => {
    if (!tenantId || !facilityId) return;
    const locIds = Array.from(new Set(rows.map((r) => r.location_id).filter(Boolean) as string[]));
    if (locIds.length === 0) return toast.error("No on-hand locations to count");
    setStarting(true);
    try {
      const name = `On-hand count ${new Date().toLocaleDateString()}`;
      const res = await runServerFn(
        () => startCountFn({ data: { tenantId, facilityId, name, locationIds: locIds } }),
        "Create cycle count",
      );
      const id = res?.id;
      if (!id) throw new Error("Failed");
      toast.success("Cycle count created");
      navigate({ to: "/app/cycle-counts/$id", params: { id } });
    } catch { /* toast handled */ } finally { setStarting(false); }
  };

  const cols: ColumnDef<Row, unknown>[] = [
    { id: "sku", header: "SKU", accessorFn: (r) => skuName(r.sku_id!),
      cell: ({ getValue }) => <span className="font-medium">{String(getValue() ?? "—")}</span> },
    { id: "loc", header: "Location", accessorFn: (r) => locCode(r.location_id!),
      cell: ({ getValue }) => <span className="font-mono text-sm">{String(getValue() ?? "—")}</span> },
    { id: "lot", header: "Lot", accessorFn: (r) => r.lot ?? "",
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.lot ?? "—"}</span> },
    { id: "qty", header: "On hand", accessorKey: "qty_on_hand",
      cell: ({ row }) => <span className="tabular-nums font-medium">{row.original.qty_on_hand}</span> },
    { id: "dos", header: "DoS", enableSorting: false,
      cell: ({ row }) => <DaysOfSupplyCell skuId={row.original.sku_id} summary={planningMap.get(row.original.sku_id)} /> },
    { id: "vel", header: "Weekly", enableSorting: false,
      cell: ({ row }) => <WeeklyVelocityCell summary={planningMap.get(row.original.sku_id)} /> },
    { id: "stockout", header: "Stockout", enableSorting: false,
      cell: ({ row }) => <ProjectedStockoutCell summary={planningMap.get(row.original.sku_id)} /> },
    { id: "rec", header: "Open rec", enableSorting: false,
      cell: ({ row }) => <OpenRecommendationCell skuId={row.original.sku_id} summary={planningMap.get(row.original.sku_id)} /> },
    { id: "risk", header: "Risk", enableSorting: false,
      cell: ({ row }) => <RiskCell skuId={row.original.sku_id} summary={planningMap.get(row.original.sku_id)} /> },
    { id: "actions", header: "", enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openCount(row.original); }}>
            <ClipboardCheck className="h-4 w-4 mr-1" />Count
          </Button>
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openXfer(row.original); }}>
            <ArrowLeftRight className="h-4 w-4 mr-1" />Transfer
          </Button>
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openAdjust(row.original); }}>
            <SlidersHorizontal className="h-4 w-4 mr-1" />Adjust
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="On Hand Inventory"
        description="Live snapshot of stock by SKU, location, and lot."
        actions={
          <div className="flex items-center gap-2">
            {tenantId ? (
              <ExportMenu
                entity="inventory_on_hand"
                tenantId={tenantId}
                facilityId={facilityId}
                customerId={customerId}
                getRows={() =>
                  (filteredRows as Row[]).map((r) => ({
                    sku: skuName(r.sku_id!),
                    location: locCode(r.location_id!),
                    lot: r.lot ?? "",
                    qty_on_hand: r.qty_on_hand,
                  }))
                }
                columns={["sku","location","lot","qty_on_hand"]}
                filters={{ search }}
                disabled={filteredRows.length === 0}
              />
            ) : null}
            {!isLoading && rows.length > 0 && (
              <Button variant="outline" size="sm" onClick={startCount} disabled={starting}>
                {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-2" />}
                Start cycle count
              </Button>
            )}
            {!isLoading && rows.length === 0 && (
              <Button variant="outline" size="sm" onClick={seed} disabled={seeding}>
                {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Seed demo inventory
              </Button>
            )}
          </div>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <ContextualTip tipKey="inventory_on_hand_intro" />
        <FilterBar search={search} onSearchChange={setSearch} searchPlaceholder="Search SKU, location, lot…" />
        <DataTable
          data={filteredRows as Row[]}
          columns={cols}
          isLoading={isLoading}
          empty={{ title: rows.length === 0 ? "No inventory yet" : "No matches",
            description: rows.length === 0 ? "Receive a shipment to populate stock." : "Try a different search term." }}
          mobileCard={(r) => (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{skuName(r.sku_id!)}</span>
                <span className="tabular-nums font-semibold">{r.qty_on_hand}</span>
              </div>
              <div className="text-xs text-muted-foreground font-mono">{locCode(r.location_id!)}{r.lot ? ` · Lot ${r.lot}` : ""}</div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => openCount(r)}><ClipboardCheck className="h-4 w-4 mr-1" />Count</Button>
                <Button size="sm" variant="outline" onClick={() => openXfer(r)}><ArrowLeftRight className="h-4 w-4 mr-1" />Transfer</Button>
              </div>
            </div>
          )}
        />
      </div>

      {/* Quick count dialog */}
      <Dialog open={!!countRow} onOpenChange={(o) => !o && setCountRow(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Count {countRow ? skuName(countRow.sku_id!) : ""}</DialogTitle></DialogHeader>
          {countRow && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Location <span className="font-mono">{locCode(countRow.location_id!)}</span>
                {countRow.lot && <> · Lot <span className="font-mono">{countRow.lot}</span></>}
                {" "}· Expected <span className="font-medium text-foreground">{countRow.qty_on_hand}</span>
              </div>
              <div className="space-y-2">
                <Label>Counted quantity</Label>
                <Input type="number" min="0" value={countQty} onChange={(e) => setCountQty(e.target.value)} autoFocus />
              </div>
              <p className="text-xs text-muted-foreground">Posting writes an inventory adjustment for the variance.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCountRow(null)}>Cancel</Button>
            <Button onClick={performCount} disabled={counting}>
              {counting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Post count
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick transfer dialog */}
      <Dialog open={!!xferRow} onOpenChange={(o) => !o && setXferRow(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Transfer {xferRow ? skuName(xferRow.sku_id!) : ""}</DialogTitle></DialogHeader>
          {xferRow && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                From <span className="font-mono">{locCode(xferRow.location_id!)}</span>
                {xferRow.lot && <> · Lot <span className="font-mono">{xferRow.lot}</span></>}
                {" "}· Available <span className="font-medium text-foreground">{xferRow.qty_on_hand}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Destination facility</Label>
                  <Select value={xferDestFac} onValueChange={(v) => { setXferDestFac(v); setXferDestLoc(""); }}>
                    <SelectTrigger><SelectValue placeholder="Facility" /></SelectTrigger>
                    <SelectContent>
                      {facilities.filter((f) => f.tenant_id === tenantId).map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Destination location</Label>
                  <Select value={xferDestLoc} onValueChange={setXferDestLoc} disabled={!xferDestFac}>
                    <SelectTrigger><SelectValue placeholder="Location" /></SelectTrigger>
                    <SelectContent>
                      {xferDestLocs.filter((l) => l.id !== xferRow.location_id).map((l) => <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input type="number" min="1" max={xferRow.qty_on_hand} value={xferQty} onChange={(e) => setXferQty(e.target.value)} />
              </div>
              <p className="text-xs text-muted-foreground">Posting writes ledger entries and closes the transfer.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setXferRow(null)}>Cancel</Button>
            <Button onClick={performXfer} disabled={xfering}>
              {xfering && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Post transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust dialog */}
      <Dialog open={!!adjRow} onOpenChange={(o) => !o && setAdjRow(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust {adjRow ? skuName(adjRow.sku_id!) : ""}</DialogTitle></DialogHeader>
          {adjRow && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Location <span className="font-mono">{locCode(adjRow.location_id!)}</span>
                {adjRow.lot && <> · Lot <span className="font-mono">{adjRow.lot}</span></>}
                {" "}· On hand <span className="font-medium text-foreground">{adjRow.qty_on_hand}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Qty delta (±)</Label>
                  <Input type="number" step="1" value={adjDelta} onChange={(e) => setAdjDelta(e.target.value)} placeholder="e.g. -2" autoFocus />
                </div>
                <div className="space-y-2">
                  <Label>Reason</Label>
                  <Select value={adjReason} onValueChange={setAdjReason}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cycle_count">Cycle count variance</SelectItem>
                      <SelectItem value="damage">Damage</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                      <SelectItem value="found">Found</SelectItem>
                      <SelectItem value="transfer_correction">Transfer correction</SelectItem>
                      <SelectItem value="returns_disposition">Returns disposition</SelectItem>
                      <SelectItem value="manual_other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea rows={3} value={adjNotes} onChange={(e) => setAdjNotes(e.target.value)} placeholder="Why this adjustment is needed (audit trail)…" />
              </div>
              <p className="text-xs text-muted-foreground">
                Adjustments greater than 100 units (absolute) require manager approval before they post to the ledger.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjRow(null)}>Cancel</Button>
            <Button onClick={performAdjust} disabled={adjusting}>
              {adjusting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}